
import type { MonarchInfo } from "../net/messages";
import { formatYang } from "../money";
import { elem, styleInstaller } from "./chrome";

const EMPIRES = [
  { id: 1, name: "Shinsoo" },
  { id: 2, name: "Chunjo" },
  { id: 3, name: "Jinno" },
] as const;

const STYLE_ID = "monarch-info-style";

const STYLE = `
.monarch-info {
  position: fixed;
  left: 50%;
  top: 18%;
  transform: translateX(-50%);
  z-index: 62;
  width: 286px;
  padding: 11px 13px 13px;
  border-radius: 10px;
  border: 1px solid rgba(225, 188, 102, 0.4);
  background: rgba(12, 14, 18, 0.95);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  pointer-events: auto;
  color: #d8dee6;
  font: 12px/1.45 system-ui, sans-serif;
}
.monarch-info[hidden] { display: none; }
.monarch-info-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}
.monarch-info-title {
  color: #e1bc66;
  font-size: 0.7rem;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}
.monarch-info-close {
  padding: 2px 8px;
  border-radius: 5px;
  border: 1px solid rgba(225, 188, 102, 0.34);
  background: rgba(225, 188, 102, 0.1);
  color: #ead8aa;
  font: inherit;
  cursor: pointer;
}
.monarch-info-seats { display: grid; gap: 4px; }
.monarch-info-seat {
  display: grid;
  grid-template-columns: 76px minmax(0, 1fr);
  gap: 8px;
  padding: 4px 6px;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.035);
}
.monarch-info-empire { color: #adbac9; }
.monarch-info-ruler {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: right;
  color: #f0e2bd;
}
.monarch-info-vacant { color: #778493; font-style: italic; }
.monarch-info-treasury {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 9px;
  padding-top: 8px;
  border-top: 1px solid rgba(225, 188, 102, 0.2);
  color: #adbac9;
}
.monarch-info-treasury-value { color: #e1bc66; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);


export class MonarchInfoPanel {
  private readonly root: HTMLElement;
  private readonly title: HTMLElement;
  private readonly rows: ReadonlyMap<number, HTMLElement>;
  private readonly treasury: HTMLElement;
  private readonly treasuryValue: HTMLElement;

  constructor(container: HTMLElement) {
    const doc = container.ownerDocument;
    installStyle(doc);

    this.root = elem(doc, "section", "monarch-info");
    this.root.hidden = true;

    const head = elem(doc, "div", "monarch-info-head");
    this.title = elem(doc, "div", "monarch-info-title");
    // `do_monarch_info`'s own two chat lines (`cmd_general.cpp:1667,1679`):
    // "My information about the emperor" for a ruler reading their own
    // treasury, "Information about the emperor" for everybody else -- set in
    // `update` from the same `treasury_visible` bit that shows the row.
    this.title.textContent = "Information about the emperor";
    const close = doc.createElement("button");
    close.type = "button";
    close.className = "monarch-info-close";
    close.textContent = "Close";
    close.addEventListener("click", () => this.close());
    head.append(this.title, close);

    const seats = elem(doc, "div", "monarch-info-seats");
    const rows = new Map<number, HTMLElement>();
    for (const empire of EMPIRES) {
      const row = elem(doc, "div", "monarch-info-seat");
      row.dataset.empire = String(empire.id);
      const label = elem(doc, "span", "monarch-info-empire", empire.name);
      const ruler = elem(doc, "span", "monarch-info-ruler monarch-info-vacant", "Vacant");
      row.append(label, ruler);
      seats.append(row);
      rows.set(empire.id, ruler);
    }
    this.rows = rows;

    this.treasury = elem(doc, "div", "monarch-info-treasury");
    this.treasury.hidden = true;
    const treasuryLabel = doc.createElement("span");
    treasuryLabel.textContent = "Royal treasury";
    this.treasuryValue = elem(doc, "span", "monarch-info-treasury-value");
    this.treasury.append(treasuryLabel, this.treasuryValue);

    this.root.append(head, seats, this.treasury);
    container.append(this.root);

    // A visible reply owns Escape just like the other board windows. The
    // listener is document-scoped because focus normally remains in the chat
    // field that sent `/minfo`, outside this read-only surface.
    doc.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !this.visible) return;
      event.preventDefault();
      event.stopPropagation();
      this.close();
    });
  }

  get visible(): boolean { return !this.root.hidden; }

  update(info: MonarchInfo): void {
    const byEmpire = new Map(info.seats.map((seat) => [seat.empire, seat.name]));
    for (const empire of EMPIRES) {
      const ruler = this.rows.get(empire.id);
      if (ruler === undefined) continue;
      const name = byEmpire.get(empire.id) ?? "";
      ruler.textContent = name || "Vacant";
      ruler.classList.toggle("monarch-info-vacant", name === "");
    }

    // `do_monarch_info`'s own two chat lines (`cmd_general.cpp:1667,1679`).
    this.title.textContent = info.treasury_visible
      ? "My information about the emperor"
      : "Information about the emperor";
    this.treasury.hidden = !info.treasury_visible;
    // Do not retain hidden money in the DOM. The server already zeroes it, and
    // clearing the node keeps a stale ruler response from surviving a later
    // public response in browser inspection or accessibility text.
    this.treasuryValue.textContent = info.treasury_visible ? formatYang(info.treasury) : "";
    this.root.hidden = false;
  }

  close(): void {
    this.root.hidden = true;
  }
}
